import React, { useState, useRef, useCallback } from 'react'

import styles from './MouseoverExploit.module.css'

interface MouseoverExploitProps {
  onTrigger: () => void
  disabled?: boolean
  duration?: number
  title: string
  description: string
}

const MouseoverExploit: React.FC<MouseoverExploitProps> = ({
  onTrigger,
  disabled = false,
  duration = 5000,
  title,
  description,
}) => {
  const [progress, setProgress] = useState(0)
  const [isHovering, setIsHovering] = useState(false)
  const [hasTriggered, setHasTriggered] = useState(false)
  const intervalRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(0)

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      cancelAnimationFrame(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    if (disabled || hasTriggered) return

    setIsHovering(true)
    startTimeRef.current = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current
      const newProgress = Math.min((elapsed / duration) * 100, 100)
      setProgress(newProgress)

      if (newProgress >= 100) {
        setHasTriggered(true)
        onTrigger()
        clearTimer()
      } else {
        intervalRef.current = requestAnimationFrame(animate)
      }
    }

    intervalRef.current = requestAnimationFrame(animate)
  }, [disabled, hasTriggered, duration, onTrigger, clearTimer])

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false)
    clearTimer()
    if (!hasTriggered) {
      setProgress(0)
    }
  }, [clearTimer, hasTriggered])

  const reset = useCallback(() => {
    setHasTriggered(false)
    setProgress(0)
  }, [])

  return (
    <div
      className={`${styles.container} ${disabled ? styles.disabled : ''} ${hasTriggered ? styles.triggered : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.progressBar} style={{ width: `${progress}%` }} />

      <div className={styles.content}>
        <div className={styles.icon}>
          {hasTriggered ? '💥' : isHovering ? '⏳' : '💣'}
        </div>
        <div className={styles.textContent}>
          <h4 className={styles.title}>{title}</h4>
          <p className={styles.description}>
            {disabled
              ? 'Extension not detected'
              : hasTriggered
                ? 'TRIGGERED! Tabs opened.'
                : isHovering
                  ? `Hold ${((duration - (progress / 100) * duration) / 1000).toFixed(1)}s...`
                  : description}
          </p>
        </div>
        {hasTriggered && (
          <button className={styles.resetBtn} onClick={reset}>
            Reset
          </button>
        )}
      </div>

      <div className={styles.warningStripes} />

      {isHovering && !hasTriggered && (
        <div className={styles.pulseRing} />
      )}

      {progress > 0 && progress < 100 && (
        <div className={styles.countdownDisplay}>
          {Math.ceil((duration - (progress / 100) * duration) / 1000)}
        </div>
      )}
    </div>
  )
}

export default MouseoverExploit
